import React, { useState, useContext } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  formHelperTextClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { v4GridContainerWidthSx } from "../../theme/v4CompatSx";
import { useToasts } from "react-toast-notifications";

import { Field, Form } from "react-final-form";

import { composeValidators, isRequired } from "../../utils/validators";

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * Styling-only: no handler, validator, state value, prop, condition or DOM node
 * moved. Every rule below is the v4 rule it replaces; each place a v5+ library
 * default would have changed the rendering is compensated for with a comment.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer` and `myAccountTitle` were declared but never
 * referenced from the JSX (copy-paste from MyAccount.js), so they rendered
 * nothing. `MuiInput` was `{ input: {...} }` — a nested key with no `&`/`@`,
 * which JSS's nested plugin ignores, so it emitted no CSS at all.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>, which takes a
// `className` but has no `sx` prop (guide §1.2).
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2, "Grid `spacing` on a container that styles its own box"). Rather
 * than re-deriving the flex arithmetic, carry v4's own declarations verbatim:
 * omit `spacing` (v9 default 0 ⇒ `gap: 0px`) and give each item the 8px
 * padding itself. Identical by construction.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({ color: theme.palette.background.ffNavy });

// was: useStyles().bodyTextSml
const bodyTextSmlSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().formContainer
//
// PARITY RESTORE (guide §2, "`<Grid container>` lost its `width: 100%`"): this
// container's direct parent is another `<Grid container>`, so in v9 — where the
// Grid container emits no width rule at all — it becomes a flex item at
// `width: auto` and shrinks to fit its content. Measured on the change-email
// form: 718.66px → 245.41px.
const formContainerSx = (theme) => ({
  ...v4GridContainerWidthSx,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
});

// was: useStyles().inputField, merged with the Grid item padding above.
//
// §1.2b: `.MuiFormHelperText-root` reaches into the TextField this file
// renders. Both convert here, so the `FfV5` prefix would make the hand-written
// v4 string stop matching — the name is read off `formHelperTextClasses`
// instead, which yields the prefixed string and fails loudly if the slot ever
// moves. `& input` / `& label` are element selectors, not `.Mui*` ones, so they
// are unaffected.
const inputFieldItemSx = {
  padding: "8px",
  "& input": {
    padding: "12px",
    border: "1px solid #a5a5a5",
    backgroundColor: "#f7f7f7",
    margin: "4px 0 10px 0",
    fontSize: "16px",
    borderRadius: "5px",
  },
  [`& .${formHelperTextClasses.root}`]: {
    fontSize: "0.9rem",
    fontWeight: "500",
    lineHeight: "1rem",
  },
  // PARITY RESTORE (T2 visual gate) — `&&`, not `&`. themeV5 carries its own
  // `MuiFormControl.styleOverrides.root` → `"& label"` (color `#171717`,
  // fontSize `1.2rem`, width `100%`), and the TextField's FormControl root is
  // an ancestor of this label, so the two rules tie at (0,1,1) and only source
  // order separates them. v4's JSS always emitted a `makeStyles` sheet after
  // MUI's own, so this rule won; emotion emits in render order and the
  // FormControl is a CHILD of the element carrying this rule, so the theme's
  // class lands later and wins instead. `&&` = (0,2,1) restores the v4 outcome
  // by specificity. Same fix as `SignIn/EmailSignIn.tsx`, where it was measured
  // in Chromium against both themes' emitted CSS. This page was not in the T2
  // capture set, but the structure is identical.
  "&& label": {
    color: "#656565",
    fontSize: "1.3rem",
    fontWeight: "500",
  },
};

// was: useStyles().footerDivider
const footerDividerSx = {
  height: "1px",
  backgroundColor: "#3c3b3b",
  margin: "5% 0 2% 0",
};

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().submit, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted the `default`
// colour and made `primary` the default, which here would paint the label lime
// on the lime `typography.button` background — an invisible button.
const submitSx = (theme) => ({
  margin: "10px 0",
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginTop: 0,
    width: "auto",
  },
  color: "text.primary",
});

export default function ChangeName() {
  const { addToast } = useToasts();

  // Account Context
  const { updating, userProfile, update, updateError, validateLogin } =
    useContext(AccountContext);

  // State
  const [nameSubmitted, setNameSubmitted] = useState(false);

  const onSubmitName = async ({ firstName, lastName }) => {
    setNameSubmitted(true);
    try {
      const updateName = await update({ firstName, lastName });
      if (updateName) {
        validateLogin();
        addToast("Name successfully updated.", {
          appearance: "success",
        });
      }
    } catch (e) {}
  };

  return (
    <>
      {userProfile && (
        <Grid container>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
              <ActiveBreadcrumbLink to="/myaccount/overview">
                Overview
              </ActiveBreadcrumbLink>
              <Typography sx={disabledBreadcrumbSx}>Change Name</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Typography variant="h2" sx={myAccountSubTitleSx}>
              Change Name
            </Typography>
            <Typography variant="body1" sx={bodyTextSx}>
              Provide your new name details here.
            </Typography>
          </Grid>
          <Grid container sx={formContainerSx}>
            {!updating && (
              <>
                <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                  {updateError && nameSubmitted && (
                    <Alert
                      severity="error"
                      variant="filled"
                      style={{ marginBottom: "0.5rem" }}
                    >
                      {updateError}
                    </Alert>
                  )}
                  <Form
                    onSubmit={onSubmitName}
                    initialValues={{
                      firstName: userProfile.firstName,
                      lastName: userProfile.lastName,
                    }}
                  >
                    {(props) => (
                      <form onSubmit={props.handleSubmit}>
                        <Grid container sx={v4GridContainerSx}>
                          <Grid size={{ xs: 12, sm: 12 }} sx={inputFieldItemSx}>
                            <Field
                              name="firstName"
                              validate={composeValidators(isRequired)}
                            >
                              {({ input, meta }) => (
                                /* PARITY RESTORE: `variant="standard"` is v4's
                                   TextField default. v5+ made "outlined" the
                                   default, which would swap the underline input
                                   for a boxed one and route the theme's
                                   `MuiInput.underline` overrides to a component
                                   that is no longer rendered. `InputLabelProps`
                                   was removed in the v9 line; the replacement is
                                   `slotProps.inputLabel`, same effect. */
                                <TextField
                                  fullWidth
                                  variant="standard"
                                  name="firstName"
                                  label="First Name"
                                  required
                                  {...input}
                                  disabled={updating}
                                  type="text"
                                  placeholder="First Name"
                                  error={meta.error && meta.touched}
                                  helperText={meta.error}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: true,
                                    },
                                  }}
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 12 }} sx={inputFieldItemSx}>
                            <Field
                              name="lastName"
                              validate={composeValidators(isRequired)}
                            >
                              {({ input, meta }) => (
                                <TextField
                                  fullWidth
                                  variant="standard"
                                  name="lastName"
                                  label="Last Name"
                                  required
                                  {...input}
                                  disabled={updating}
                                  type="text"
                                  placeholder="Last Name"
                                  error={meta.error && meta.touched}
                                  helperText={meta.error}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: true,
                                    },
                                  }}
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 12 }} sx={v4GridItemSx}>
                            <Button
                              sx={submitSx}
                              type="submit"
                              disabled={updating}
                            >
                              Update Name
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    )}
                  </Form>
                  <Divider sx={footerDividerSx} />
                  <Typography variant="body1" sx={bodyTextSmlSx}>
                    <span>Tip</span> - Tell us your name, we&apos;d like to know
                    you!
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}></Grid>
              </>
            )}
            {updating && (
              <Box sx={{ m: 5 }} align="center">
                <CircularProgress variant="indeterminate"></CircularProgress>
                <Typography variant="body2" sx={bodyTextSmlSx}>
                  <br />
                  Updating your Name. Please do not refresh your browser.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
}
